'use client'

import { ListHeader, Image } from '@macarongfactory/web-ui'
import Link from 'next/link'
import { useRef } from 'react'

import { ROUTES } from '@/shared/lib/routes'
import { OptimizedImage } from '@/shared/ui'

import ContentStats from './ContentStats'
import { carStoryTracking } from '../lib/tracking'
import { type IMycleContentTrending } from '../model'
import useTrackOnVisible from '../model/useTrackOnVisible'

interface ITrendingStoriesSectionProps {
  items: IMycleContentTrending[]
}

const TrendingStoriesSection = ({ items }: ITrendingStoriesSectionProps) => {
  const sectionRef = useRef<HTMLElement | null>(null)
  useTrackOnVisible({
    ref: sectionRef,
    tracker: carStoryTracking.showTrendingSection,
    enabled: items.length > 0,
  })

  if (items.length === 0) return null

  return (
    <section ref={sectionRef} className='pb-dimension-x10 flex flex-col'>
      <ListHeader
        title={
          <ListHeader.Main.Title.Text
            text='지금 뜨는 이야기'
            typography='heading-3-bold'
          />
        }
      />
      <ul className='gap-y-dimension-x4 gap-x-dimension-x3 px-dimension-x5 grid grid-cols-2'>
        {items.map(item => (
          <li key={item.serverId}>
            <TrendingStoryCard item={item} />
          </li>
        ))}
      </ul>
    </section>
  )
}

const TrendingStoryCard = ({ item }: { item: IMycleContentTrending }) => {
  const cardRef = useRef<HTMLAnchorElement | null>(null)
  useTrackOnVisible({
    ref: cardRef,
    tracker: () => carStoryTracking.showTrendingCard(item.title),
  })

  return (
    <Link
      ref={cardRef}
      href={ROUTES.CAR_STORY.CONTENT_DETAIL(item.serverId)}
      onClick={() => carStoryTracking.clickTrendingCard(item.title)}
      className='gap-y-dimension-x2-5 flex flex-col'
    >
      <div className='bg-background-neutral-weak rounded-dimension-x3 relative aspect-square w-full overflow-hidden'>
        <OptimizedImage
          src={item.thumbnailUrl}
          alt={item.title}
          width='100%'
          height='100%'
          radius={0}
          scaletype='crop'
          sizes='(max-width: 480px) 50vw, 240px'
          fallback={<Image.Fallback />}
        />
      </div>
      <div className='gap-y-dimension-x1-5 flex flex-col'>
        <p className='text-title-2-medium text-foreground-neutral-strong line-clamp-2 h-dimension-x11 whitespace-pre-line'>
          {item.title}
        </p>
        <ContentStats stat={item.stat} />
      </div>
    </Link>
  )
}

export default TrendingStoriesSection
